import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
} from '@mui/material';

function JsonTable({ rows, columns }) {
  if (!rows?.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No data in this section.
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key}>{col.label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={index}>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render ? col.render(row) : (row[col.key] ?? '—')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MaterialTakeoffReportSections({ resultJson, buyItems = [] }) {
  const data = resultJson || {};

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {data.summary && (
        <Alert severity="info" sx={{ py: 0.5 }}>
          {data.summary}
        </Alert>
      )}

      <Box>
        <Typography variant="subtitle2" gutterBottom>Raw cut list</Typography>
        {(data.raw_cut_list || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">No raw cut list entries.</Typography>
        ) : (
          (data.raw_cut_list || []).map((profile, index) => (
            <Box key={index} sx={{ mb: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>{profile.profile || 'Profile'}</Typography>
              <JsonTable
                rows={profile.cuts || []}
                columns={[
                  { key: 'length_display', label: 'Length' },
                  { key: 'quantity', label: 'Qty' },
                ]}
              />
            </Box>
          ))
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Nesting review</Typography>
        {(data.nesting_review || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">No nesting review data.</Typography>
        ) : (
          (data.nesting_review || []).map((row, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>{row.profile}</Typography>
              {(row.sticks || []).map((stick) => (
                <Typography key={stick.stick_number} variant="body2" sx={{ ml: 1 }}>
                  Stick {stick.stick_number}: {(stick.cuts || []).join(', ') || '—'}
                </Typography>
              ))}
            </Box>
          ))
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Buy list</Typography>
        <JsonTable
          rows={buyItems.length ? buyItems : (data.buy_list || [])}
          columns={[
            { key: 'line_text', label: 'Line' },
            { key: 'quantity', label: 'Qty' },
            { key: 'unit', label: 'Unit' },
            { key: 'stock_length_ft', label: 'Stock (ft)' },
          ]}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Sheet metal summary</Typography>
        <JsonTable
          rows={data.sheet_metal_summary || []}
          columns={[
            { key: 'thickness_display', label: 'Thickness' },
            { key: 'sq_ft', label: 'Sq ft' },
            { key: 'recommended_sheets', label: 'Sheets' },
            { key: 'sheet_size', label: 'Sheet size' },
          ]}
        />
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Weight estimate</Typography>
        {data.weight_estimate ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1 }}>
            {['structural_lb', 'plate_lb', 'pipe_lb', 'purchased_lb', 'total_lb'].map((key) => (
              <Typography key={key} variant="body2">
                {key.replace('_lb', '').replace('_', ' ')}: {data.weight_estimate[key] ?? '—'} lb
              </Typography>
            ))}
            {data.weight_estimate.assumptions && (
              <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '1 / -1' }}>
                {data.weight_estimate.assumptions}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">No weight estimate.</Typography>
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Red flags</Typography>
        {(data.red_flags || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">No red flags.</Typography>
        ) : (
          (data.red_flags || []).map((flag, index) => (
            <Alert key={index} severity={flag.severity === 'high' ? 'error' : flag.severity === 'medium' ? 'warning' : 'info'} sx={{ mb: 0.5 }}>
              {flag.message}
              {flag.source_page ? ` (page ${flag.source_page})` : ''}
            </Alert>
          ))
        )}
      </Box>

      <Box>
        <Typography variant="subtitle2" gutterBottom>Purchased item audit</Typography>
        <JsonTable
          rows={data.purchased_item_audit || []}
          columns={[
            { key: 'item', label: 'Item' },
            { key: 'source_sheet', label: 'Sheet' },
            { key: 'extended_qty', label: 'Extended qty' },
            {
              key: 'verification_status',
              label: 'Status',
              render: (row) => (
                <Chip
                  size="small"
                  label={row.verification_status || '—'}
                  color={row.verification_status === 'verified' ? 'success' : 'warning'}
                  variant="outlined"
                />
              ),
            },
          ]}
        />
      </Box>
    </Box>
  );
}
